import React, { useState, useMemo, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import { orderLineService } from "/app/src/services";
import { useTranslation } from "react-i18next";
import { OrderLine as OrderLineType } from "/app/src/models";
import { createColumnHelper } from "@tanstack/react-table";
import Table from "/app/src/components/generic/tables/table";
import { useSortUpgrade } from "/app/src/hooks/useSortUpgrade";
import usePaginatedData from "/app/src/hooks/usePaginatedData";
import useQueryParams from "/app/src/hooks/useQueryParams";
import {
  Button,
  Checkbox,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@heroui/react";

import useSetting from "/app/src/components/settings/setting";
import {
  buildExplorerColumns,
  isColumnSelected,
  toggleExplorerColumn,
} from "/app/src/components/explorer/helpers/columns";
import HeroSearch from "/app/src/components/generic/components/heroSearch";
import Breadcrumbs from "/app/src/components/generic/components/breadcrumbs";
import DateTime from "/app/src/components/generic/formatting/dateTime";
import { useAuthState } from "/app/src/contexts/authentication";

/**
 * Renders the OrderLines component, which displays a paginated and sortable table of order lines.
 *
 * @returns {JSX.Element} The rendered OrderLines component.
 */
export default function OrderLines() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [page, setPage] = useState(
    parseInt(searchParams.get("page") || "1") - 1,
  );
  const [pageSize, setPageSize] = useState(
    parseInt(searchParams.get("pageSize") || "25"),
  );
  const { user } = useAuthState();
  const columnHelper = createColumnHelper<OrderLineType>();

  const availableColumns = [
    { id: "number", label: t("translation:number") },
    { id: "Info1", label: t("translation:info1") },
    { id: "id", label: t("translation:id") },
    { id: "modifiedDate", label: t("translation:creation_date") },
    { id: "status", label: t("translation:status") },
  ];

  const { settingValue, createUpdateSetting } = useSetting({
    name: "explorerColumns",
    userId: user?.id,
  });

  const isColumnChecked = useCallback(
    (id: string) => isColumnSelected(settingValue, "order_line", id),
    [settingValue],
  );

  const toggleColumnSelection = useCallback(
    async (id: string) =>
      toggleExplorerColumn(
        settingValue,
        createUpdateSetting,
        "order_line",
        id,
        user?.id,
      ),
    [settingValue, createUpdateSetting],
  );

  const columns = useMemo(() => {
    /**
     * Creates a column definition based on the given id.
     * @param {string} id - The identifier of the column to create.
     * @returns {Object|null} The column definition object or null if the id is not recognized.
     */
    const makeColumn = (id: string) => {
      switch (id) {
        case "id":
          return columnHelper.accessor("id", {
            id: "id",
            cell: (info) => info.getValue(),
            header: () => t("translation:id"),
          });
        case "number":
          return columnHelper.accessor("number", {
            id: "number",
            cell: (info) => info.getValue(),
            header: () => t("translation:number"),
          });
        case "Info1":
          return columnHelper.accessor("Info1", {
            id: "Info1",
            cell: (info) => info.getValue(),
            header: () => t("translation:info1"),
          });
        case "materialName":
          return columnHelper.accessor("materialName", {
            id: "materialName",
            cell: (info) => info.getValue(),
            header: () => t("translation:material_name"),
          });
        case "quantity":
          return columnHelper.accessor("quantity", {
            id: "quantity",
            cell: (info) => info.getValue(),
            header: () => t("translation:quantity"),
          });
        case "orderName":
          return columnHelper.accessor("orderName", {
            id: "orderName",
            cell: (info) => info.getValue(),
            header: () => t("translation:order_name"),
          });
        case "modifiedDate":
          return columnHelper.accessor("modifiedDate", {
            id: "modifiedDate",
            cell: (info) => <DateTime date={info.getValue()} />,
            header: () => t("translation:creation_date"),
          });
        case "status":
          return columnHelper.accessor("status", {
            id: "status",
            cell: (info) => info.getValue(),
            header: () => t("translation:status"),
          });
        default:
          return null;
      }
    };
    return buildExplorerColumns(settingValue, "order_line", makeColumn, [
      "orderName",
      "materialName",
      "quantity",
    ]);
  }, [settingValue, columnHelper, t]);

  const [sort, setSort] = useSortUpgrade([]);
  const [searchString, setSearchString] = useState(
    searchParams.get("search") || "",
  );

  const goToOrderLine = useCallback(
    (orderLineId: number) => {
      navigate(`/explorer/order_lines/${orderLineId}`);
    },
    [navigate],
  );

  const {
    data: orderLines,
    count: orderLinesCount,
    isFetching,
  } = usePaginatedData<OrderLineType>({
    queryKey: ["order_lines"],
    searchString,
    sort,
    page,
    pageSize,
    service: orderLineService,
    options: {
      state: "queued",
    },
  });

  const { updatePage, updatePageSize } = useQueryParams({
    count: orderLinesCount,
    isFetching,
    page,
    setPage,
    pageSize,
    setPageSize,
  });

  const createToggleHandler = useCallback(
    (id: string) => () => {
      toggleColumnSelection(id);
    },
    [toggleColumnSelection],
  );

  return (
    <div className="explorer">
      <Helmet>
        <title>{t("translation:order_lines")} - ItemPath</title>
      </Helmet>
      <Breadcrumbs
        items={[
          { label: "Explorer", to: "/explorer" },
          { label: t("translation:order_lines") },
        ]}
      />

      <div className="mt-12 mb-4 flex justify-between">
        <div className="flex justify-between gap-2">
          <HeroSearch
            className="w-[300px]"
            setSearchString={setSearchString}
            allowClear
            box={false}
          />
        </div>
        <Popover placement="bottom" radius="md" aria-label="Customize columns">
          <PopoverTrigger>
            <Button size="md" radius="md" color="primary">
              {t("translation:customize_columns")}
            </Button>
          </PopoverTrigger>
          <PopoverContent>
            <div className="w-[300px] p-4">
              <h3 className="text-[16px]">
                {t("translation:available_columns")}
              </h3>
              <div className="mt-2 flex flex-col gap-2">
                {availableColumns.map((column) => (
                  <Checkbox
                    size="sm"
                    key={column.id}
                    defaultSelected={isColumnChecked(column.id)}
                    onChange={createToggleHandler(column.id)}
                  >
                    {column.label}
                  </Checkbox>
                ))}
              </div>
            </div>
          </PopoverContent>
        </Popover>
      </div>
      <Table
        loading={isFetching}
        rows={orderLines}
        tableColumns={columns}
        length={orderLinesCount}
        sort={sort}
        setSort={setSort}
        paginationEnabled={{
          currentPage: page,
          pageSize,
          setPage: updatePage,
          setPageSize: updatePageSize,
        }}
        rowClicked={goToOrderLine}
        emptyText={t("translation:no_order_lines")}
      />
    </div>
  );
}
